import { useState, useEffect, useMemo, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Car, Plus, Trash2, Pencil, Download, Settings as SettingsIcon } from 'lucide-react';
import { toast } from 'sonner';

const MONTHS_FR = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
const fmtEur = (v) => `${(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const fmtKm = (v) => `${(v || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} km`;
const todayISO = () => new Date().toISOString().slice(0, 10);
const formatDateFR = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

export default function MileagePage() {
  const [entries, setEntries] = useState([]);
  const [clients, setClients] = useState([]);
  const [totals, setTotals] = useState({ total_km: 0, total_eur: 0, rate_per_km: 0.40 });
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null); // entry object when editing
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fetchAll = useCallback(async (y = year) => {
    setLoading(true);
    try {
      const [entriesRes, clientsRes] = await Promise.all([
        api.get(`/mileage?year=${y}`),
        api.get('/clients/names'),
      ]);
      setEntries(entriesRes.data.entries || []);
      setTotals({
        total_km: entriesRes.data.total_km || 0,
        total_eur: entriesRes.data.total_eur || 0,
        rate_per_km: entriesRes.data.rate_per_km || 0.40,
      });
      setClients(clientsRes.data || []);
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { fetchAll(year); }, [year, fetchAll]);

  // Group entries by YYYY-MM for display
  const grouped = useMemo(() => {
    const map = {};
    for (const e of entries) {
      const ym = (e.date || '').slice(0, 7);
      if (!ym) continue;
      if (!map[ym]) map[ym] = [];
      map[ym].push(e);
    }
    return Object.keys(map).sort().reverse().map(ym => ({
      ym,
      label: `${MONTHS_FR[parseInt(ym.split('-')[1], 10) - 1]} ${ym.split('-')[0]}`,
      entries: map[ym],
      total_km: map[ym].reduce((s, e) => s + (parseFloat(e.km) || 0), 0),
    }));
  }, [entries]);

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette entrée ?')) return;
    try {
      await api.delete(`/mileage/${id}`);
      toast.success('Entrée supprimée');
      fetchAll(year);
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/mileage/export/pdf?year=${year}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `frais_km_${year}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success('PDF téléchargé');
    } catch {
      toast.error('Erreur génération PDF');
    } finally {
      setExporting(false);
    }
  };

  const currentYear = new Date().getFullYear();
  const yearOptions = [];
  for (let y = currentYear; y >= currentYear - 5; y--) yearOptions.push(y);

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="mileage-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <Car className="w-7 h-7 text-amber-400" />
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-zinc-100" style={{ fontFamily: 'Cabinet Grotesk' }}>
              Frais kilométriques
            </h1>
            <p className="text-xs sm:text-sm text-zinc-500 mt-0.5">
              Saisissez vos déplacements — export PDF prêt pour le comptable.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v, 10))}>
            <SelectTrigger className="w-[120px] bg-zinc-950 border-zinc-800 text-zinc-200 rounded-sm" data-testid="year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-200">
              {yearOptions.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={() => setSettingsOpen(true)}
            className="border-zinc-700 text-zinc-300 rounded-sm hover:border-amber-500/40"
            data-testid="settings-btn"
          >
            <SettingsIcon className="w-4 h-4 mr-2" /> Tarif €/km
          </Button>
          <Button
            variant="outline"
            onClick={handleExportPdf}
            disabled={exporting || entries.length === 0}
            className="border-zinc-700 text-zinc-300 rounded-sm hover:border-amber-500/40"
            data-testid="export-pdf-btn"
          >
            <Download className="w-4 h-4 mr-2" /> {exporting ? 'Génération…' : 'Export PDF'}
          </Button>
          <Button
            onClick={() => { setEditing(null); setFormOpen(true); }}
            className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm font-medium"
            data-testid="add-entry-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Ajouter
          </Button>
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatCard label="Total kilomètres" value={fmtKm(totals.total_km)} testId="stat-total-km" />
        <StatCard label={`Total ${year} (à ${totals.rate_per_km.toFixed(2)} €/km)`} value={fmtEur(totals.total_eur)} testId="stat-total-eur" />
        <StatCard label="Nombre d'entrées" value={entries.length} testId="stat-count" />
      </div>

      {/* Body */}
      {loading ? (
        <div className="text-center text-zinc-500 py-10">Chargement…</div>
      ) : grouped.length === 0 ? (
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-10 text-center" data-testid="empty-state">
          <Car className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-400 mb-1">Aucune entrée pour {year}.</p>
          <p className="text-zinc-600 text-sm">Cliquez sur "Ajouter" pour saisir votre premier déplacement.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {grouped.map(group => (
            <div key={group.ym} className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden" data-testid={`month-${group.ym}`}>
              <div className="px-4 py-2.5 border-b border-zinc-800 flex items-center justify-between bg-zinc-950/40">
                <h2 className="font-semibold text-zinc-200 text-sm" style={{ fontFamily: 'Cabinet Grotesk' }}>
                  {group.label}
                </h2>
                <div className="text-xs text-zinc-400 flex items-center gap-3">
                  <span>{group.entries.length} entrée(s)</span>
                  <span className="font-semibold text-amber-300">{fmtKm(group.total_km)}</span>
                  <span className="text-zinc-300">· {fmtEur(group.total_km * totals.rate_per_km)}</span>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-[11px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
                    <tr>
                      <th className="text-left px-4 py-2 font-medium">Date</th>
                      <th className="text-left px-4 py-2 font-medium">Client</th>
                      <th className="text-right px-4 py-2 font-medium">Km</th>
                      <th className="text-left px-4 py-2 font-medium">Notes</th>
                      <th className="text-right px-4 py-2 font-medium">€ HT</th>
                      <th className="text-right px-4 py-2 font-medium w-20">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.entries.map(e => (
                      <tr key={e.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30" data-testid={`entry-row-${e.id}`}>
                        <td className="px-4 py-2 text-zinc-300 whitespace-nowrap">{formatDateFR(e.date)}</td>
                        <td className="px-4 py-2 text-zinc-200">{e.client_name || '—'}</td>
                        <td className="px-4 py-2 text-zinc-300 text-right whitespace-nowrap">{fmtKm(e.km)}</td>
                        <td className="px-4 py-2 text-zinc-500 max-w-[280px] truncate" title={e.notes}>{e.notes || ''}</td>
                        <td className="px-4 py-2 text-zinc-300 text-right whitespace-nowrap">{fmtEur((parseFloat(e.km) || 0) * totals.rate_per_km)}</td>
                        <td className="px-4 py-2 text-right whitespace-nowrap">
                          <button onClick={() => { setEditing(e); setFormOpen(true); }} className="text-zinc-400 hover:text-amber-400 p-1" data-testid={`edit-${e.id}`} title="Modifier">
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDelete(e.id)} className="text-zinc-400 hover:text-rose-400 p-1 ml-1" data-testid={`delete-${e.id}`} title="Supprimer">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {formOpen && (
        <EntryFormDialog
          open={formOpen}
          onOpenChange={setFormOpen}
          editing={editing}
          clients={clients}
          rate={totals.rate_per_km}
          onSaved={() => { setFormOpen(false); setEditing(null); fetchAll(year); }}
        />
      )}
      {settingsOpen && (
        <SettingsDialog
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
          initialRate={totals.rate_per_km}
          onSaved={() => { setSettingsOpen(false); fetchAll(year); }}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, testId }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4" data-testid={testId}>
      <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{label}</p>
      <p className="text-xl sm:text-2xl font-bold text-zinc-50" style={{ fontFamily: 'Cabinet Grotesk' }}>{value}</p>
    </div>
  );
}

function EntryFormDialog({ open, onOpenChange, editing, clients, rate, onSaved }) {
  const [form, setForm] = useState(() => ({
    date: editing?.date || todayISO(),
    client_id: editing?.client_id || '',
    client_name: editing?.client_name || '',
    km: editing?.km != null ? String(editing.km) : '',
    notes: editing?.notes || '',
  }));
  const [saving, setSaving] = useState(false);

  // When user picks a client from the selector, denormalise the name automatically
  const handleClientChange = (id) => {
    if (id === '__free__') {
      setForm(f => ({ ...f, client_id: '', client_name: '' }));
      return;
    }
    const c = clients.find(x => x.id === id);
    setForm(f => ({ ...f, client_id: id, client_name: c?.company_name || '' }));
  };

  const save = async () => {
    if (!form.date) { toast.error('Date requise'); return; }
    if (!form.client_name.trim()) { toast.error('Client requis'); return; }
    const kmNum = parseFloat(form.km);
    if (!kmNum || kmNum <= 0) { toast.error('Km > 0 requis'); return; }
    setSaving(true);
    try {
      const payload = {
        date: form.date,
        client_id: form.client_id || null,
        client_name: form.client_name.trim(),
        km: kmNum,
        notes: form.notes.trim(),
      };
      if (editing) {
        await api.put(`/mileage/${editing.id}`, payload);
        toast.success('Entrée modifiée');
      } else {
        await api.post('/mileage', payload);
        toast.success('Entrée ajoutée');
      }
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  const kmNum = parseFloat(form.km) || 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-lg" data-testid="entry-form-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Cabinet Grotesk' }}>
            <Car className="w-5 h-5 text-amber-400" />
            {editing ? 'Modifier le déplacement' : 'Nouveau déplacement'}
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            Le coût €/km est appliqué automatiquement depuis les paramètres.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs text-zinc-400">Date *</Label>
            <Input
              type="date"
              value={form.date}
              onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))}
              className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm mt-1"
              data-testid="form-date"
            />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Client *</Label>
            <Select value={form.client_id || '__free__'} onValueChange={handleClientChange}>
              <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-200 rounded-sm mt-1" data-testid="form-client">
                <SelectValue placeholder="Sélectionner un client" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-200 max-h-60">
                <SelectItem value="__free__">— Saisie libre —</SelectItem>
                {clients.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!form.client_id && (
              <Input
                placeholder="Nom du client (saisie libre)"
                value={form.client_name}
                onChange={(e) => setForm(f => ({ ...f, client_name: e.target.value }))}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm mt-1"
                data-testid="form-client-free"
              />
            )}
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Kilomètres *</Label>
            <Input
              type="number"
              min="0"
              step="1"
              value={form.km}
              onChange={(e) => setForm(f => ({ ...f, km: e.target.value }))}
              className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm mt-1"
              placeholder="Ex: 120"
              data-testid="form-km"
            />
            {kmNum > 0 && (
              <p className="text-[11px] text-zinc-500 mt-1">
                Estimation : <span className="text-amber-300 font-medium">{fmtEur(kmNum * rate)}</span> ({rate.toFixed(2)} €/km)
              </p>
            )}
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Notes <span className="text-zinc-600">(optionnel)</span></Label>
            <Textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
              className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm mt-1 resize-none"
              placeholder="Ex: Visite atelier, livraison pièces…"
              data-testid="form-notes"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-zinc-700 text-zinc-300 rounded-sm">Annuler</Button>
          <Button onClick={save} disabled={saving} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm font-medium" data-testid="save-entry-btn">
            {saving ? 'Enregistrement…' : editing ? 'Enregistrer' : 'Ajouter'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SettingsDialog({ open, onOpenChange, initialRate, onSaved }) {
  const [rate, setRate] = useState(String(initialRate ?? 0.40));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const r = parseFloat(rate);
    if (Number.isNaN(r) || r < 0) { toast.error('Tarif invalide'); return; }
    setSaving(true);
    try {
      await api.put('/mileage/settings', { rate_per_km: r });
      toast.success('Tarif mis à jour');
      onSaved?.();
    } catch {
      toast.error('Erreur');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-md" data-testid="settings-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Cabinet Grotesk' }}>
            <SettingsIcon className="w-5 h-5 text-amber-400" /> Tarif kilométrique
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            Tarif appliqué à toutes les entrées au moment du calcul.
          </DialogDescription>
        </DialogHeader>
        <div className="py-3">
          <Label className="text-xs text-zinc-400">Coût par kilomètre (€)</Label>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm mt-1"
            data-testid="settings-rate"
          />
          <p className="text-[11px] text-zinc-500 mt-2">
            Barème fiscal 2026 : ≈ 0,52 €/km pour 5 CV (jusqu'à 5 000 km/an). Adaptez selon votre véhicule.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-zinc-700 text-zinc-300 rounded-sm">Annuler</Button>
          <Button onClick={save} disabled={saving} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm font-medium" data-testid="save-settings-btn">
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
